/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  OktaServerSuggestionApplication,
  OktaServerSuggestionApplication$inboundSchema,
} from "./oktaserversuggestionapplication.js";
import {
  OktaServerSuggestionRemote,
  OktaServerSuggestionRemote$inboundSchema,
} from "./oktaserversuggestionremote.js";

/**
 * open: not yet acted on; dismissed: an administrator dismissed it; installed: a live MCP server in one of the organization's projects fronts one of the entry's URLs (trailing slashes ignored; legacy deployment attachments are not consulted). Installed outranks dismissed.
 */
export const OktaServerSuggestionState = {
  Open: "open",
  Dismissed: "dismissed",
  Installed: "installed",
} as const;
/**
 * open: not yet acted on; dismissed: an administrator dismissed it; installed: a live MCP server in one of the organization's projects fronts one of the entry's URLs (trailing slashes ignored; legacy deployment attachments are not consulted). Installed outranks dismissed.
 */
export type OktaServerSuggestionState = ClosedEnum<
  typeof OktaServerSuggestionState
>;

/**
 * A Speakeasy-owned catalog entry whose Okta mapping matches at least one active, assigned application in the organization's Okta snapshot. Suggest only; nothing is created until the administrator accepts through the remote MCP install flow.
 */
export type OktaServerSuggestion = {
  description: string;
  /**
   * When the suggestion was dismissed; present while a dismissal is recorded, whatever the state.
   */
  dismissedAt?: Date | undefined;
  /**
   * Public documentation URL when the entry has one.
   */
  documentationUrl?: string | undefined;
  /**
   * HTTPS URL of the entry's icon when it has one.
   */
  iconUrl?: string | undefined;
  /**
   * The entry URLs the organization already fronts with a live MCP server.
   */
  installedUrls: Array<string>;
  /**
   * Matching Okta application instances, by label.
   */
  oktaApplications: Array<OktaServerSuggestionApplication>;
  /**
   * Catalog entry id; the key for dismiss and restore.
   */
  registryEntryId: string;
  /**
   * Remote endpoints in catalog order.
   */
  remotes: Array<OktaServerSuggestionRemote>;
  /**
   * Catalog server name, for example io.example/linear; also the registry specifier for catalog lookups.
   */
  serverName: string;
  /**
   * open: not yet acted on; dismissed: an administrator dismissed it; installed: a live MCP server in one of the organization's projects fronts one of the entry's URLs (trailing slashes ignored; legacy deployment attachments are not consulted). Installed outranks dismissed.
   */
  state: OktaServerSuggestionState;
  /**
   * Whether the catalog records that the server's authorization server supports OAuth dynamic client registration; install flows default to per-user OAuth when true.
   */
  supportsDcr: boolean;
  /**
   * Catalog display title when the entry has one.
   */
  title?: string | undefined;
  /**
   * Vendor Issuer URL for Cross App Access when the catalog knows it.
   */
  xaaIssuer?: string | undefined;
};

/** @internal */
export const OktaServerSuggestionState$inboundSchema: z.ZodMiniEnum<
  typeof OktaServerSuggestionState
> = z.enum(OktaServerSuggestionState);

/** @internal */
export const OktaServerSuggestion$inboundSchema: z.ZodMiniType<
  OktaServerSuggestion,
  unknown
> = z.pipe(
  z.object({
    description: z.string(),
    dismissed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    documentation_url: z.optional(z.string()),
    icon_url: z.optional(z.string()),
    installed_urls: z.array(z.string()),
    okta_applications: z.array(OktaServerSuggestionApplication$inboundSchema),
    registry_entry_id: z.string(),
    remotes: z.array(OktaServerSuggestionRemote$inboundSchema),
    server_name: z.string(),
    state: OktaServerSuggestionState$inboundSchema,
    supports_dcr: z.boolean(),
    title: z.optional(z.string()),
    xaa_issuer: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "dismissed_at": "dismissedAt",
      "documentation_url": "documentationUrl",
      "icon_url": "iconUrl",
      "installed_urls": "installedUrls",
      "okta_applications": "oktaApplications",
      "registry_entry_id": "registryEntryId",
      "server_name": "serverName",
      "supports_dcr": "supportsDcr",
      "xaa_issuer": "xaaIssuer",
    });
  }),
);

export function oktaServerSuggestionFromJSON(
  jsonString: string,
): SafeParseResult<OktaServerSuggestion, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => OktaServerSuggestion$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'OktaServerSuggestion' from JSON`,
  );
}
